import { useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../../store/hooks";
import { useShoppingCart } from "../context/ShoppingCartContext";
import usePageLanguage from "../hooks/usePageLanguage";
import { pf, resolveAbsoluteAssetUrl } from "../utilities/helpers";
import {
  getCatalogProductDisplayFields,
  getProductPageBaseHref,
  resolveCatalogProductPricing,
} from "../utilities/catalogProductAdapter";
import {
  buildCatalogProductDetailHref,
  getVariantPriceRange,
  hasVariants,
} from "../utilities/productCatalog";
import type { Product } from "../interfaces/catalogProduct";
import useLazyLoad from "../utilities/useLazyLoad";
import React from "react";

interface Props {
  product: Product;
}

const languageTranslations = {
  et: {
    add_to_cart: "LISA OSTUKORVI",
    view_product: "VAATA TOODET",
  },
  en: {
    add_to_cart: "ADD TO CART",
    view_product: "VIEW PRODUCT",
  },
};

export default function ProductComponent({ product }: Props) {
  const { increaseCartQuantity, pricingNow } = useShoppingCart();
  const lang = usePageLanguage();
  const locale = usePageLanguage();

  const parentPricing = useMemo(
    () => resolveCatalogProductPricing(product, pricingNow),
    [product, pricingNow]
  );

  const display = useMemo(
    () => getCatalogProductDisplayFields(product, lang),
    [product, lang]
  );
  const displayImageUrl = useMemo(
    () => resolveAbsoluteAssetUrl(display.firstFileUrl),
    [display.firstFileUrl]
  );

  const productItemRef = useRef<HTMLDivElement>(null);
  const pages = useAppSelector((state) => state.builder.pages.list);
  const loaded = useLazyLoad(productItemRef, displayImageUrl);
  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );
  const productPage = pages.find(
    (page) => page.pageType === "product" && page.languageCode === lang
  );

  const languageProductHref = getProductPageBaseHref(productPage, languages);

  const productDetailHref = useMemo(
    () => buildCatalogProductDetailHref(languageProductHref, product, lang),
    [languageProductHref, product, lang]
  );

  const priceRange = useMemo(() => getVariantPriceRange(product), [product]);
  const variantMode = hasVariants(product);

  const typographies = useAppSelector((state) => state.builder.typographies);
  const colors = useAppSelector((state) => state.builder.colors);

  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isHeadingHovered, setIsHeadingHovered] = useState(false);
  const [isPriceHovered, setIsPriceHovered] = useState(false);

  const filteredTypographyButton = typographies.filter((x) => {
    if (
      x.activeComponents &&
      x.activeComponents.includes("productsGridButton")
    ) {
      return x;
    } else {
      return null;
    }
  });

  const filteredTypographyPrice = typographies.filter((x) => {
    if (
      x.activeComponents &&
      x.activeComponents.includes("productsGridPrice")
    ) {
      return x;
    } else {
      return null;
    }
  });

  const filteredTypographyHeading = typographies.filter((x) => {
    if (
      x.activeComponents &&
      x.activeComponents.includes("productsGridHeading")
    ) {
      return x;
    } else {
      return null;
    }
  });

  const typographyButtonStyles =
    filteredTypographyButton.length > 0
      ? {
          fontFamily: filteredTypographyButton[0].fontFamily,
          fontSize:
            filteredTypographyButton[0].fontSize +
            filteredTypographyButton[0].fontSizeUnit,
          fontStyle: filteredTypographyButton[0].fontStyle,
          fontWeight: filteredTypographyButton[0].fontWeight,
          letterSpacing: filteredTypographyButton[0].letterSpacing,
          lineHeight: filteredTypographyButton[0].lineHeight,
          color: colors.find((x) => x.id === filteredTypographyButton[0].colorId)
            ?.color,
          ...(isButtonHovered && {
            color: colors.find(
              (x) => x.id === filteredTypographyButton[0].linkHoverColorId
            )?.color,
          }),
        }
      : {};

  const typographyHeadingStyles =
    filteredTypographyHeading.length > 0
      ? {
          fontFamily: filteredTypographyHeading[0].fontFamily,
          fontSize:
            filteredTypographyHeading[0].fontSize +
            filteredTypographyHeading[0].fontSizeUnit,
          fontStyle: filteredTypographyHeading[0].fontStyle,
          fontWeight: filteredTypographyHeading[0].fontWeight,
          letterSpacing: filteredTypographyHeading[0].letterSpacing,
          lineHeight: filteredTypographyHeading[0].lineHeight,
          color: colors.find(
            (x) => x.id === filteredTypographyHeading[0].colorId
          )?.color,
          ...(isHeadingHovered && {
            color: colors.find(
              (x) => x.id === filteredTypographyHeading[0].linkHoverColorId
            )?.color,
          }),
        }
      : {};

  const typographyPriceStyles =
    filteredTypographyPrice.length > 0
      ? {
          fontFamily: filteredTypographyPrice[0].fontFamily,
          fontSize:
            filteredTypographyPrice[0].fontSize +
            filteredTypographyPrice[0].fontSizeUnit,
          fontStyle: filteredTypographyPrice[0].fontStyle,
          fontWeight: filteredTypographyPrice[0].fontWeight,
          letterSpacing: filteredTypographyPrice[0].letterSpacing,
          lineHeight: filteredTypographyPrice[0].lineHeight,
          color: colors.find((x) => x.id === filteredTypographyPrice[0].colorId)
            ?.color,
          ...(isPriceHovered && {
            color: colors.find(
              (x) => x.id === filteredTypographyPrice[0].linkHoverColorId
            )?.color,
          }),
        }
      : {};

  const imageBackground =
    loaded && loaded.length
      ? loaded.map((src) => `url("${src}")`).join(", ")
      : "none";

  return (
    <div className="productcol">
      <div className="web-product" ref={productItemRef}>
        <a
          href={productDetailHref}
          className="product-img"
          style={{
            backgroundImage: imageBackground,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPosition: "center",
          }}
        >
          <img
            src={displayImageUrl || ""}
            alt={display.displayName + " pilt."}
            style={{ width: "100%" }}
          />
          <div className="product-info-overlay">
            <div
              dangerouslySetInnerHTML={{
                __html: display.shortDescription,
              }}
            />
          </div>
        </a>
        <div className="padding-10 pb-20">
          <div className="product-name">
            <a
              style={typographyHeadingStyles}
              onMouseEnter={() => setIsHeadingHovered(true)}
              onMouseLeave={() => setIsHeadingHovered(false)}
              href={productDetailHref}
            >
              {display.displayName}&nbsp;
            </a>
          </div>
          <div
            className="productPrice"
            style={typographyPriceStyles}
            onMouseEnter={() => setIsPriceHovered(true)}
            onMouseLeave={() => setIsPriceHovered(false)}
          >
            {!variantMode && (
              <>
                <span
                  style={
                    parentPricing.activeAdjustments.length > 0
                      ? { textDecoration: "line-through" }
                      : {}
                  }
                >
                  {parentPricing.activeAdjustments.length > 0 ? (
                    <>{pf(product.priceRegular)}&euro;</>
                  ) : (
                    <>{pf(parentPricing.price_total)}&euro;</>
                  )}
                </span>
                {parentPricing.activeAdjustments.length > 0 ? (
                  <span className="discount-price-color">
                    {pf(Number(parentPricing.price_total))}&euro;
                  </span>
                ) : null}
              </>
            )}
            {variantMode && priceRange && (
              <span>
                {priceRange.minPrice !== priceRange.maxPrice ? (
                  <>
                    {pf(priceRange.minPrice)} &euro; - {pf(priceRange.maxPrice)}{" "}
                    &euro;
                  </>
                ) : (
                  <>{pf(priceRange.minPrice)} &euro;</>
                )}
              </span>
            )}
          </div>
          <div>
            <button
              className="btn btn-primary addProductToCart shop-controlled-border-radius"
              style={typographyButtonStyles}
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!variantMode) {
                  increaseCartQuantity(
                    product.productUuid ?? "",
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                  );
                } else {
                  window.location.href = productDetailHref;
                }
              }}
            >
              {!variantMode
                ? languageTranslations[locale]?.add_to_cart
                : languageTranslations[locale]?.view_product}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
